import React, { useState } from 'react';
import axios from 'axios';
import { AlertCircle, X } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

const DISMISS_KEY = 'unverified-email-banner-dismissed';

type ResendState = 'idle' | 'sending' | 'sent' | 'error';

const UnverifiedEmailBanner: React.FC = () => {
  const { user, isAuthenticated, token } = useAuthStore();

  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const [resendState, setResendState] = useState<ResendState>('idle');
  const [resendMessage, setResendMessage] = useState<string>('');

  // Soft enforcement: only show the banner for users whose email is explicitly
  // false. Legacy users with emailVerified === undefined are treated as verified.
  if (!isAuthenticated || !user || user.emailVerified !== false || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Ignore storage errors — banner will still dismiss for this render.
    }
    setDismissed(true);
  };

  const handleResend = async () => {
    if (!token) return;
    setResendState('sending');
    setResendMessage('');
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await axios.post(
        `${apiUrl}/auth/resend-verification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResendState('sent');
      setResendMessage(response.data?.message || 'Verification email sent. Check your inbox.');
    } catch (err: unknown) {
      setResendState('error');
      if (axios.isAxiosError(err)) {
        setResendMessage(
          err.response?.data?.message ||
            'Could not send verification email. Please try again later.'
        );
      } else {
        setResendMessage('Could not send verification email. Please try again later.');
      }
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-amber-50 border-b border-amber-200 text-amber-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" aria-hidden="true" />
        <p className="text-sm flex-1 min-w-0">
          Please verify your email address.
          {resendState === 'sent' && resendMessage && (
            <span className="ml-2 text-green-700">{resendMessage}</span>
          )}
          {resendState === 'error' && resendMessage && (
            <span className="ml-2 text-red-700">{resendMessage}</span>
          )}
        </p>
        <button
          type="button"
          onClick={handleResend}
          disabled={resendState === 'sending' || resendState === 'sent'}
          className="text-sm font-medium text-amber-900 underline hover:text-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {resendState === 'sending'
            ? 'Sending…'
            : resendState === 'sent'
              ? 'Sent'
              : 'Resend verification email'}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="text-amber-700 hover:text-amber-900 p-1 -m-1"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default UnverifiedEmailBanner;
